/**
 * CalendarCore — 캘린더 그리드 + 헤더 드릴다운 (내부 전용)
 *
 * DatePicker / DateTimePicker / DateRangePicker / DateTimeRangePicker 가 공유하는
 * 캘린더 코어. 팝오버/트리거/시간선택 등 바깥 UI는 각 컴포넌트가 담당하고,
 * 이 컴포넌트는 "날짜 그리드 + 월/년 드릴다운 + 선택/범위 하이라이트"만 렌더한다.
 *
 * 배럴(index.ts)로 노출하지 않는다 — 내부 구현 세부.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from './cn'
import { getCalendarDays, isSameDay } from './DatePicker'
import styles from './CalendarCore.module.css'

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']
const MONTHS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']
const YEARS_PER_PAGE = 12

export interface CalendarCoreProps {
  /** 선택 모드. 'single'=단일 날짜, 'range'=시작~종료 */
  mode?: 'single' | 'range'
  /** single 모드에서 선택된 날짜 */
  selected?: Date | null
  /** range 모드 시작일 */
  rangeStart?: Date | null
  /** range 모드 종료일 */
  rangeEnd?: Date | null
  /** range 모드에서 종료일 선택 중 hover 프리뷰 날짜 */
  hoverDate?: Date | null
  /** range 모드 hover 콜백 */
  onHoverDate?: (date: Date | null) => void
  /** 날짜(day) 선택 콜백 */
  onSelectDay: (date: Date) => void
  /**
   * 월(month) 자체가 값인 모드 (DatePicker mode='month').
   * true면 day 뷰 없이 month 뷰가 최하위이며, 월 선택이 곧 확정이다.
   */
  monthMode?: boolean
  /** monthMode에서 월 확정 콜백 */
  onSelectMonth?: (year: number, month: number) => void
  /** 최소 선택 가능 날짜 */
  minDate?: Date
  /** 최대 선택 가능 날짜 */
  maxDate?: Date
  /** 초기 표시 기준 날짜 (없으면 오늘). 이 값이 바뀌면 뷰가 해당 월로 이동 */
  focusDate?: Date | null
  /** 추가 클래스명 */
  className?: string
}

type View = 'day' | 'month' | 'year'

/** 날짜를 그날 00:00 로 정규화 */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export function CalendarCore({
  mode = 'single',
  selected,
  rangeStart,
  rangeEnd,
  hoverDate,
  onHoverDate,
  onSelectDay,
  monthMode = false,
  onSelectMonth,
  minDate,
  maxDate,
  focusDate,
  className,
}: CalendarCoreProps) {
  const today = useMemo(() => new Date(), [])

  const [view, setView] = useState<View>(monthMode ? 'month' : 'day')
  const [viewYear, setViewYear] = useState(() => (focusDate ?? today).getFullYear())
  const [viewMonth, setViewMonth] = useState(() => (focusDate ?? today).getMonth())

  // focusDate(값)가 실제로 바뀔 때만 뷰를 그 달로 동기화한다.
  // 매 렌더 새 Date 객체가 오므로 timestamp로 메모이즈해 사용자의 월 이동을 되돌리지 않는다.
  const focusTime = focusDate?.getTime() ?? null
  useEffect(() => {
    if (focusTime != null) {
      const d = new Date(focusTime)
      setViewYear(d.getFullYear())
      setViewMonth(d.getMonth())
    }
  }, [focusTime])

  const calendarDays = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth])

  // ── min/max 비활성 판정 ──
  const isDisabledDate = useCallback((d: Date) => {
    if (minDate && d < startOfDay(minDate)) return true
    if (maxDate && d > startOfDay(maxDate)) return true
    return false
  }, [minDate, maxDate])

  const isDisabledMonth = useCallback((year: number, month: number) => {
    if (minDate) {
      const monthEnd = new Date(year, month + 1, 0)
      if (monthEnd < startOfDay(minDate)) return true
    }
    if (maxDate) {
      const monthStart = new Date(year, month, 1)
      if (monthStart > startOfDay(maxDate)) return true
    }
    return false
  }, [minDate, maxDate])

  const isDisabledYear = useCallback((year: number) => {
    if (minDate && year < minDate.getFullYear()) return true
    if (maxDate && year > maxDate.getFullYear()) return true
    return false
  }, [minDate, maxDate])

  // ── 네비게이션 (뷰별로 이동 단위가 다름) ──
  const handlePrev = useCallback(() => {
    if (view === 'day') {
      setViewMonth((m) => {
        if (m === 0) { setViewYear((y) => y - 1); return 11 }
        return m - 1
      })
    } else if (view === 'month') {
      setViewYear((y) => y - 1)
    } else {
      setViewYear((y) => y - YEARS_PER_PAGE)
    }
  }, [view])

  const handleNext = useCallback(() => {
    if (view === 'day') {
      setViewMonth((m) => {
        if (m === 11) { setViewYear((y) => y + 1); return 0 }
        return m + 1
      })
    } else if (view === 'month') {
      setViewYear((y) => y + 1)
    } else {
      setViewYear((y) => y + YEARS_PER_PAGE)
    }
  }, [view])

  // 헤더 타이틀 클릭 → 상위 뷰로 드릴다운 (day→month→year)
  const handleHeaderClick = useCallback(() => {
    setView((v) => (v === 'day' ? 'month' : v === 'month' ? 'year' : 'year'))
  }, [])

  const handleSelectMonth = useCallback((month: number) => {
    if (isDisabledMonth(viewYear, month)) return
    if (monthMode) {
      onSelectMonth?.(viewYear, month)
      return
    }
    setViewMonth(month)
    setView('day')
  }, [isDisabledMonth, monthMode, onSelectMonth, viewYear])

  const handleSelectYear = useCallback((year: number) => {
    if (isDisabledYear(year)) return
    setViewYear(year)
    setView('month')
  }, [isDisabledYear])

  const handleSelectDay = useCallback((d: Date) => {
    if (isDisabledDate(d)) return
    onSelectDay(d)
  }, [isDisabledDate, onSelectDay])

  // ── range 하이라이트 계산 ──
  // 종료일이 아직이면 hoverDate로 프리뷰. lo<=hi 로 정규화.
  const [rangeLo, rangeHi] = useMemo(() => {
    if (mode !== 'range') return [null, null] as [Date | null, Date | null]
    const start = rangeStart ? startOfDay(rangeStart) : null
    const rawEnd = rangeEnd ?? hoverDate
    const end = rawEnd ? startOfDay(rawEnd) : null
    if (start && end) return start <= end ? [start, end] : [end, start]
    return [start, null]
  }, [mode, rangeStart, rangeEnd, hoverDate])

  const yearPageStart = viewYear - (((viewYear % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE)
  const headerTitle =
    view === 'day' ? `${viewYear}년 ${viewMonth + 1}월` :
    view === 'month' ? `${viewYear}년` :
    `${yearPageStart} ~ ${yearPageStart + YEARS_PER_PAGE - 1}`

  return (
    <div className={cn(styles.calendar, className)}>
      {/* 헤더: prev / 드릴다운 타이틀 / next */}
      <div className={styles.header}>
        <button type="button" className={styles.navBtn} onClick={handlePrev} aria-label="이전">
          <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
            <path d="M10.354 3.646a.5.5 0 010 .708L6.707 8l3.647 3.646a.5.5 0 01-.708.708l-4-4a.5.5 0 010-.708l4-4a.5.5 0 01.708 0z" />
          </svg>
        </button>
        <button
          type="button"
          className={styles.titleBtn}
          onClick={handleHeaderClick}
          disabled={view === 'year'}
        >
          {headerTitle}
        </button>
        <button type="button" className={styles.navBtn} onClick={handleNext} aria-label="다음">
          <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
            <path d="M5.646 3.646a.5.5 0 01.708 0l4 4a.5.5 0 010 .708l-4 4a.5.5 0 01-.708-.708L9.293 8 5.646 4.354a.5.5 0 010-.708z" />
          </svg>
        </button>
      </div>

      {/* day 뷰 */}
      {view === 'day' && (
        <>
          <div className={styles.weekdays}>
            {WEEKDAYS.map((w) => (
              <span key={w} className={styles.weekday}>{w}</span>
            ))}
          </div>
          <div
            className={styles.daysGrid}
            onMouseLeave={mode === 'range' ? () => onHoverDate?.(null) : undefined}
          >
            {calendarDays.map(({ date, currentMonth }, i) => {
              const dayStart = startOfDay(date)
              const isToday = isSameDay(date, today)
              const isDayDisabled = isDisabledDate(date)

              let isSelected = false
              let isRangeStart = false
              let isRangeEnd = false
              let isInRange = false
              if (mode === 'range') {
                if (rangeLo && isSameDay(dayStart, rangeLo)) isRangeStart = true
                if (rangeHi && isSameDay(dayStart, rangeHi)) isRangeEnd = true
                if (rangeLo && rangeHi && dayStart > rangeLo && dayStart < rangeHi) isInRange = true
              } else {
                isSelected = selected ? isSameDay(date, selected) : false
              }

              return (
                <button
                  key={i}
                  type="button"
                  className={cn(
                    styles.dayCell,
                    !currentMonth && styles.dayOtherMonth,
                    isToday && styles.dayToday,
                    (isSelected || isRangeStart || isRangeEnd) && styles.daySelected,
                    isInRange && styles.dayInRange,
                    isRangeStart && styles.dayRangeStart,
                    isRangeEnd && styles.dayRangeEnd,
                    isDayDisabled && styles.dayDisabled,
                  )}
                  onClick={() => handleSelectDay(date)}
                  onMouseEnter={mode === 'range' ? () => onHoverDate?.(date) : undefined}
                  disabled={isDayDisabled}
                >
                  {date.getDate()}
                </button>
              )
            })}
          </div>
        </>
      )}

      {/* month 뷰 */}
      {view === 'month' && (
        <div className={styles.monthsGrid}>
          {MONTHS.map((mLabel, month) => {
            const isSelectedMonth =
              monthMode && selected != null && selected.getFullYear() === viewYear && selected.getMonth() === month
            const isThisMonth = today.getFullYear() === viewYear && today.getMonth() === month
            const disabled = isDisabledMonth(viewYear, month)
            return (
              <button
                key={month}
                type="button"
                className={cn(
                  styles.monthCell,
                  isThisMonth && styles.dayToday,
                  isSelectedMonth && styles.daySelected,
                  disabled && styles.dayDisabled,
                )}
                onClick={() => handleSelectMonth(month)}
                disabled={disabled}
              >
                {mLabel}
              </button>
            )
          })}
        </div>
      )}

      {/* year 뷰 */}
      {view === 'year' && (
        <div className={styles.monthsGrid}>
          {Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i).map((year) => {
            const isThisYear = today.getFullYear() === year
            const disabled = isDisabledYear(year)
            return (
              <button
                key={year}
                type="button"
                className={cn(
                  styles.monthCell,
                  isThisYear && styles.dayToday,
                  viewYear === year && styles.daySelected,
                  disabled && styles.dayDisabled,
                )}
                onClick={() => handleSelectYear(year)}
                disabled={disabled}
              >
                {year}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
